import { useContext, useEffect, useState } from "react";
import SelectInputWithSearch from "../../../layouts/ui/SelectInputWithSearch";
import { IoMdAdd } from "react-icons/io";
import { MdOutlineClear, MdDeleteOutline } from "react-icons/md";
import SkillsDetails from "../../../data/skills.json";
import { DataProvider } from "../../../store/ContextProvider";
import { createLanguage } from "../../../services/Language/createLanguage";
import { updateLanguage } from "../../../services/Language/updateLanguage";
import { deleteLanguage } from "../../../services/Language/deleteLanguage";
import { FiEdit2 } from "react-icons/fi";
import SelectInput from "../../../layouts/ui/SelectInput";

// Helper to create empty skill object
const makeEmptySkill = (overrides = {}) => ({
    name: overrides.name ?? "",
    proficiency: overrides.proficiency ?? "",
    read: overrides.read ?? false,
    write: overrides.write ?? false,
    speak: overrides.speak ?? false,
});

export const Language = ({ Information, errors, setErrors, candidate }) => {
    const { BeURL, showAlert } = useContext(DataProvider);
    const [language, setLanguage] = useState([]);
    const [topInput, setTopInput] = useState(makeEmptySkill());
    const [editingCardId, setEditingCardId] = useState(null);
    // Load candidate languages
    useEffect(() => {
        if (candidate?.languages?.length) {
            setLanguage(candidate.languages);
        }
    }, [candidate]);

    // Field validation
    const checkValidation = (field, value) => {
        const e = {};
        let isValid = true;

        if (!value?.trim()) {
            e[field] = "This field is required!";
            isValid = false;
        } else {
            e[field] = "";
        }

        setErrors((prev) => ({ ...prev, ...e }));
        return isValid;
    };

    // Input change handler
    const handleTopFieldChange = (field, value) => {
        setTopInput((prev) => ({ ...prev, [field]: value }));
        if (["name", "proficiency"].includes(field)) {
            checkValidation(field, value);
        }
    };
    // Add new language
    const handleTopAdd = async () => {
        const validName = checkValidation("name", topInput.name);
        const validProficiency = checkValidation("proficiency", topInput.proficiency);


        if (!validName || !validProficiency) {
            showAlert("Kindly select the Language and Proficiency.");
            return;
        }
        createLanguage({ BeURL, topInput, setLanguage, showAlert, handleTopClear })
    };

    // Clear input
    const handleTopClear = () => {
        setTopInput(makeEmptySkill());
        setEditingCardId(null);
    };

    // Delete card
    const handleCardDelete = (id) => {
        deleteLanguage({ BeURL, showAlert, setLanguage, id })
    };

    // Edit card
    const handleCardEdit = (idx) => {
        const card = language[idx];
        if (!card) return;
        setTopInput(makeEmptySkill({ ...card }));
        setEditingCardId(card._id)
    };

    // Update card
    const handleTopUpdate = (editingCardId) => {
        if (!editingCardId) return;
        const validName = checkValidation("name", topInput.name);
        const validProficiency = checkValidation("proficiency", topInput.proficiency);

        if (!validName || !validProficiency) {
            alert("Kindly select the Language and Proficiency.");
            return;
        }
        updateLanguage({ BeURL, topInput, showAlert, setLanguage, editingCardId, handleTopClear })


    };

    return (
        <div className="py-4">
            {/* Top input panel */}
            <div className="border-2 rounded-2xl p-4 bg-white shadow-sm">
                <div className="flex items-start justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">Languages</h4>
                    <div className="ml-3 flex items-center gap-2">
                        {editingCardId ? (
                            <>
                                <button
                                    onClick={handleTopClear}
                                    className="text-primary-400 underline capitalize font-medium cursor-pointer"
                                >
                                    <MdOutlineClear />

                                </button>
                                <button
                                    onClick={() => handleTopUpdate(editingCardId)}
                                    className=" px-3 py-1  rounded-lg bg-primary-400 cursor-pointer text-white flex items-center justify-center shadow-md gap-2"
                                >
                                    <FiEdit2 size={15} />
                                    <span className=" text-sm">Update</span>

                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleTopClear}
                                    className="underline text-primary-300 font-medium cursor-pointer"
                                >
                                    Clear
                                </button>
                                <button
                                    onClick={handleTopAdd}
                                    className="px-2 rounded-lg bg-primary-400 text-white flex items-center justify-center shadow-md cursor-pointer"
                                >
                                    <IoMdAdd size={20} />
                                    <span className="hidden md:block">Add</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Inputs row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 items-center">
                    <SelectInputWithSearch
                        label="Language"
                        mandatory
                        value={topInput.name}
                        onChange={(e) => handleTopFieldChange("name", e.target.value)}
                        placeholder="language"
                        options={SkillsDetails.languages || []}
                        className="w-full"
                        error={errors.name}
                        border={true}
                    />
                    <SelectInput
                        label="Proficiency"
                        mandatory
                        value={topInput.proficiency}
                        onChange={(e) => handleTopFieldChange("proficiency", e.target.value)}
                        options={SkillsDetails.proficiency}
                        placeholder="Proficiency"
                        error={errors.proficiency}
                        className={"w-full"}
                        border={true}
                    />
                    <div className="flex items-center gap-4 mt-3">
                        {["read", "write", "speak"].map((field) => (
                            <label key={field} className="flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    checked={topInput[field]}
                                    onChange={(e) => handleTopFieldChange(field, e.target.checked)}
                                />
                                {field.charAt(0).toUpperCase() + field.slice(1)}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Language cards */}
            <div className="mt-3 space-y-4 bg-white rounded-2xl px-2 py-3 w-full">
                {language || language.length > 0 ? (
                    <div className="flex flex-wrap justify-evenly gap-3">
                        {language.map((card, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-lg shadow-md p-4 relative grid"
                            >
                                <div className="flex justify-between gap-10 items-start">
                                    <div>
                                        <div className="text-xs text-slate-400">Language</div>
                                        <div className="font-medium capitalize">{card.name || "-"}</div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleCardEdit(idx)}
                                            className="text-slate-600 p-1"
                                            title="Edit"
                                        >
                                            <FiEdit2 />
                                        </button>
                                        <button
                                            onClick={() => handleCardDelete(card?._id)}
                                            className="text-red-500 p-1"
                                            title="Delete"
                                        >
                                            <MdDeleteOutline />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-2 text-sm text-slate-600">
                                    {["Read", "Write", "Speak"]
                                        .filter((f) => card[f.toLowerCase()])
                                        .join(" • ") || "-"}
                                </div>

                                <div className="mt-1 text-xs text-slate-400">
                                    <span>Proficiency: </span>
                                    <span className="font-medium">
                                        {card.proficiency || "-"}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-slate-500 italic">
                        No languages yet. Use the form above to add.
                    </div>
                )}
            </div>
        </div>
    );
};
